// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/ShipmentProfile.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI, vendorsAPI } from "../../services/api";
import { usePermission } from "../../hooks/usePermission";
import { gregorianToJalaliStr } from "../../utils/PersianDatePicker";
import SearchableSelect from "../../components/SearchableSelect";
import { ArrowRight, Edit2, Trash2, Plus, Package, Warehouse, User, AlertTriangle, CheckCircle2, Clock, X } from "lucide-react";
import DistributionForm from "./DistributionForm";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);

const STATUS_LABELS: Record<string, string> = {
  in_customs: "در گمرک",
  clearing:   "در حال ترخیص",
  cleared:    "ترخیص شده",
  distributed:"توزیع شده",
};

const STATUS_COLORS: Record<string, string> = {
  in_customs: "#f59e0b",
  clearing:   "#3b82f6",
  cleared:    "#06b6d4",
  distributed:"#10b981",
};

interface Props {
  shipmentId: number;
  onBack: () => void;
  onEdit: (id: number) => void;
}

export default function ShipmentProfile({ shipmentId, onBack, onEdit }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canEdit = usePermission("production", "edit");
  const canDelete = usePermission("production", "delete");
  const canAdd = usePermission("production", "add");

  const [shipment, setShipment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [distItem, setDistItem] = useState<any>(null);  // برای ساخت توزیع
  const [editDist, setEditDist] = useState<any>(null);   // برای ویرایش توزیع
  const [toast, setToast] = useState({ text: "", ok: true });
  // 🆕 برای انتخاب فروشنده هنگام ترخیص
  const [vendorModal, setVendorModal] = useState<{ status: string } | null>(null);
  const [vendors, setVendors] = useState<any[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>("");

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await trackingAPI.getShipment(shipmentId);
    if (r.success) setShipment(r.shipment);
    setLoading(false);
  }, [shipmentId]);

  useEffect(() => { load(); }, [load]);

  const handleChangeStatus = async (newStatus: string) => {
    const labels = { in_customs: "در گمرک", clearing: "در حال ترخیص", cleared: "ترخیص شده", distributed: "توزیع شده" };
    
    // 🆕 اگر تغییر به cleared است و قبلاً خرید خودکار نداریم، باید فروشنده پرسیده شود
    if (newStatus === "cleared" && !shipment?.auto_purchase_id) {
      // لیست فروشندگان را لود کن (اگر هنوز لود نشده)
      if (vendors.length === 0) {
        const v = await vendorsAPI.list();
        if (v.success) setVendors(v.vendors || []);
      }
      // مودال را باز کن
      setSelectedVendorId("");
      setVendorModal({ status: newStatus });
      return;
    }
    
    // برای سایر حالت‌ها (یا اگر خرید خودکار قبلاً ساخته شده)، مستقیم تغییر بده
    if (!window.confirm(`تغییر وضعیت بار به "${labels[newStatus as keyof typeof labels]}"؟`)) return;
    const r = await trackingAPI.changeStatus(shipmentId, newStatus, newStatus === "cleared" ? new Date().toISOString().split("T")[0] : undefined);
    if (r.success) { showToast("وضعیت تغییر کرد"); load(); }
    else showToast(r.message || "خطا", false);
  };
  
  // 🆕 تأیید تغییر وضعیت با فروشنده انتخاب شده
  const confirmStatusWithVendor = async () => {
    if (!vendorModal) return;
    if (!selectedVendorId) {
      showToast("لطفاً فروشنده را انتخاب کنید", false);
      return;
    }
    const r = await trackingAPI.changeStatus(
      shipmentId,
      vendorModal.status,
      new Date().toISOString().split("T")[0],
      parseInt(selectedVendorId)
    );
    if (r.success) {
      showToast("وضعیت تغییر کرد و خرید خودکار ساخته شد");
      setVendorModal(null);
      setSelectedVendorId("");
      load();
    } else {
      showToast(r.message || "خطا", false);
    }
  };

  const handleDeleteDist = async (id: number) => {
    if (!window.confirm("حذف این توزیع؟\nاگر به گدام رفته، تأثیر آن از موجودی گدام کم می‌شود.")) return;
    const r = await trackingAPI.deleteDistribution(id);
    if (r.success) { showToast("توزیع حذف شد"); load(); }
    else showToast(r.message || "خطا", false);
  };

  if (loading || !shipment) {
    return <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>;
  }

  // اگر در حال ساخت توزیع
  if (distItem) {
    return (
      <DistributionForm
        item={distItem}
        editing={null}
        onBack={() => { setDistItem(null); load(); }}
      />
    );
  }
  if (editDist) {
    return (
      <DistributionForm
        item={editDist.item}
        editing={editDist.dist}
        onBack={() => { setEditDist(null); load(); }}
      />
    );
  }

  const color = STATUS_COLORS[shipment.status];

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.text}
        </div>
      )}

      {/* هدر */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
          <ArrowRight size={15} /> بازگشت
        </button>
        <span style={{ padding: "5px 14px", borderRadius: 8, background: `${color}20`, color, fontSize: 13, fontWeight: 800, fontFamily: "monospace" }}>
          {shipment.serial}
        </span>
        <span style={{ padding: "4px 12px", borderRadius: 7, background: `${color}15`, color, fontSize: 12, fontWeight: 700 }}>
          {STATUS_LABELS[shipment.status]}
        </span>
        {shipment.bar_number && <span style={{ fontSize: 14, color: T.text, fontWeight: 700 }}>📦 {shipment.bar_number}</span>}
        <div style={{ marginRight: "auto", display: "flex", gap: 8 }}>
          {canEdit && (
            <button onClick={() => onEdit(shipmentId)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 16px", borderRadius: 10, border: "none", background: "rgba(59,130,246,0.15)", color: "#3b82f6", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
              <Edit2 size={13} /> ویرایش بار
            </button>
          )}
        </div>
      </div>

      {/* اطلاعات بار */}
      <div style={{ padding: 20, borderRadius: 14, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}`, borderRight: `4px solid ${color}`, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
          <Info label="گمرک" value={shipment.customs_office_name} />
          <Info label="تاریخ ورود" value={gregorianToJalaliStr(shipment.entry_date)} />
          <Info label="تاریخ ترخیص" value={shipment.clearance_date ? gregorianToJalaliStr(shipment.clearance_date) : "—"} />
          <Info label="نوع مالکیت" value={shipment.ownership_type === "transit" ? "🚛 ترانزیت" : "🏢 شرکت ما"} />
          {shipment.ownership_type === "transit" && (
            <>
              <Info label="صاحب جنس" value={shipment.transit_owner || "—"} />
              <Info label="شماره BL" value={shipment.bl_number || "—"} />
            </>
          )}
          <Info label="آی نمبر" value={shipment.i_number || "—"} mono />
          <Info label="ای نمبر" value={shipment.e_number || "—"} mono />
          <Info label="شماره کانتینر" value={shipment.container_number || "—"} mono />
          <Info label="پلیت موتر" value={shipment.truck_plate || "—"} mono />
        </div>
        {shipment.notes && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 9, background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc", fontSize: 12, color: T.text }}>
            💬 {shipment.notes}
          </div>
        )}

        {/* تغییر سریع وضعیت */}
        {canEdit && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.cardBorder}`, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.subtext }}>تغییر سریع وضعیت:</span>
            {["in_customs", "clearing", "cleared"].map(st => (
              shipment.status !== st && (
                <button key={st} onClick={() => handleChangeStatus(st)} style={{
                  padding: "6px 14px", borderRadius: 8, border: `1px solid ${STATUS_COLORS[st]}40`,
                  background: `${STATUS_COLORS[st]}15`, color: STATUS_COLORS[st],
                  cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                }}>
                  → {STATUS_LABELS[st]}
                </button>
              )
            ))}
          </div>
        )}
      </div>

      {/* اجناس + توزیع */}
      <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 14 }}>📦 اجناس بار و توزیع</div>

      {(shipment.items || []).map((item: any) => {
        const remaining = item.remaining_qty;
        const canDistribute = shipment.status === "cleared" && remaining > 0.001;
        const isFullyDist = remaining < 0.001;

        return (
          <div key={item.id} style={{
            padding: 18, borderRadius: 14, marginBottom: 14,
            background: dark ? "rgba(255,255,255,0.03)" : "#fff",
            border: `1px solid ${T.cardBorder}`,
          }}>
            {/* هدر آیتم */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14, paddingBottom: 14, borderBottom: `1px dashed ${T.cardBorder}` }}>
              <Package size={20} color="#f97316" />
              <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{item.product_name}</span>
              <span style={{ padding: "3px 10px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.06)" : "#f1f5f9", color: T.subtext, fontSize: 11, fontWeight: 700 }}>
                {item.unit}
              </span>
              <div style={{ display: "flex", gap: 16, marginRight: "auto", alignItems: "center", flexWrap: "wrap" }}>
                <Stat label="مقدار اولیه" value={fmt(item.quantity)} color={T.text} />
                <Stat label="توزیع شده" value={fmt(item.distributed_qty)} color="#3b82f6" />
                <Stat label="باقی‌مانده" value={fmt(remaining)} color={isFullyDist ? "#10b981" : "#f59e0b"} />
                {canDistribute && canAdd && (
                  <button onClick={() => setDistItem(item)} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "8px 16px", borderRadius: 9, border: "none",
                    background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff",
                    cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                  }}>
                    <Plus size={13} /> توزیع جنس
                  </button>
                )}
              </div>
            </div>

            {/* لیست توزیع‌ها */}
            {item.distributions.length === 0 ? (
              <div style={{ textAlign: "center", padding: 20, color: T.subtext, fontSize: 12 }}>
                {shipment.status === "cleared"
                  ? "هنوز توزیعی ثبت نشده. روی «توزیع جنس» کلیک کنید."
                  : `برای ثبت توزیع، ابتدا وضعیت بار را به "ترخیص شده" تغییر دهید.`}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {item.distributions.map((d: any) => {
                  const hasShortage = d.shortage > 0.001;
                  return (
                    <div key={d.id} style={{
                      padding: "10px 14px", borderRadius: 9,
                      background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                      border: `1px solid ${T.cardBorder}`,
                      borderRight: `3px solid ${d.destination_type === "warehouse" ? "#10b981" : "#8b5cf6"}`,
                      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                    }}>
                      {d.destination_type === "warehouse" ? (
                        <>
                          <Warehouse size={16} color="#10b981" />
                          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{d.warehouse_name}</span>
                          <span style={{ padding: "2px 8px", borderRadius: 5, background: "rgba(16,185,129,0.15)", color: "#10b981", fontSize: 10, fontWeight: 700 }}>گدام</span>
                        </>
                      ) : (
                        <>
                          <User size={16} color="#8b5cf6" />
                          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{d.customer_name}</span>
                          <span style={{ padding: "2px 8px", borderRadius: 5, background: "rgba(139,92,246,0.15)", color: "#8b5cf6", fontSize: 10, fontWeight: 700 }}>مشتری</span>
                        </>
                      )}

                      <span style={{ fontSize: 12, color: T.subtext }}>📅 {gregorianToJalaliStr(d.distribution_date)}</span>

                      <div style={{ marginRight: "auto", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: T.subtext }}>اولیه: <strong style={{ color: T.text }}>{fmt(d.expected_qty)}</strong></span>
                        <span style={{ fontSize: 12, color: hasShortage ? "#10b981" : T.text }}>رسیده: <strong>{fmt(d.received_qty)}</strong></span>
                        {hasShortage && (
                          <span style={{ padding: "3px 10px", borderRadius: 7, background: "rgba(239,68,68,0.15)", color: "#ef4444", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", gap: 4 }}>
                            <AlertTriangle size={11} /> کسری: {fmt(d.shortage)}
                          </span>
                        )}
                        {canEdit && (
                          <button onClick={() => setEditDist({ item, dist: d })} title="ویرایش" style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "rgba(59,130,246,0.15)", color: "#3b82f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Edit2 size={12} />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => handleDeleteDist(d.id)} title="حذف" style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "rgba(239,68,68,0.15)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>

                      {(d.notes || d.shortage_reason) && (
                        <div style={{ width: "100%", fontSize: 11, color: T.subtext, fontStyle: "italic", paddingTop: 6, borderTop: `1px dashed ${T.cardBorder}` }}>
                          {d.shortage_reason && <span style={{ color: "#ef4444" }}>علت کسری: {d.shortage_reason} • </span>}
                          {d.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      
      {/* 🆕 مودال انتخاب فروشنده برای خرید خودکار */}
      {vendorModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }} onClick={() => setVendorModal(null)}>
          <div style={{
            background: dark ? "#1e293b" : "#ffffff",
            borderRadius: 14, padding: 24, width: "100%", maxWidth: 480,
            border: `1px solid ${T.cardBorder}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 22 }}>🚚</span>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: T.text, margin: 0 }}>تغییر وضعیت به ترخیص شده</h3>
              </div>
              <button onClick={() => setVendorModal(null)} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: T.subtext, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: 14, borderRadius: 10, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#d97706", fontWeight: 700, marginBottom: 8 }}>📌 خرید خودکار</div>
              <div style={{ fontSize: 11, color: T.subtext, lineHeight: 1.7 }}>
                با تغییر وضعیت به <strong>«ترخیص شده»</strong>، یک خرید جدید با همین اقلام و وضعیت <strong>«در انتظار»</strong> در منوی جورنال ساخته می‌شود.
                قیمت‌ها در حالت اولیه <strong>صفر</strong> است؛ بعداً نرخ‌ها را وارد کرده و خرید را تأیید کنید.
              </div>
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>
              فروشنده برای خرید خودکار <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <SearchableSelect
              options={vendors.map((v: any) => ({
                value: String(v.id),
                label: `🏢 ${[v.first_name, v.last_name, v.company_name].filter(Boolean).join(' ')}${v.currency ? ` (${v.currency})` : ''}`
              }))}
              value={selectedVendorId}
              onChange={v => setSelectedVendorId(v)}
              placeholder="انتخاب فروشنده..."
            />
            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <button onClick={() => setVendorModal(null)} style={{
                padding: "10px 18px", borderRadius: 8,
                border: `1px solid ${T.cardBorder}`, background: "transparent",
                color: T.text, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
              }}>انصراف</button>
              <button onClick={confirmStatusWithVendor} disabled={!selectedVendorId} style={{
                padding: "10px 18px", borderRadius: 8, border: "none",
                background: selectedVendorId ? "linear-gradient(135deg,#06b6d4,#3b82f6)" : "rgba(0,0,0,0.1)",
                color: "white", cursor: selectedVendorId ? "pointer" : "not-allowed",
                fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                opacity: selectedVendorId ? 1 : 0.5,
              }}>تأیید و ترخیص</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: mono ? "monospace" : "inherit" }}>{value}</div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: T.subtext }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}